import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { PopUpNav } from '../../../../components/popup-nav';
import { IconDelete, IconEdit } from '../../../../assets/popup-nav-icons';
import { ROUTES } from '../../../../routes/routes';
import { useLavvaCreateActionContext } from '../../../../integrations/lavva/modules/action-create/context';
import { useActionDetailsPresentation } from '../../../../integrations/lavva/modules/action-create/hooks/use-action-details-presentation';
import { DeviceTaskParams, SceneTaskType, Task, TaskVariant } from '../../../../integrations/lavva/modules/action-create/types';
import { actionTaskVariants, actionTasks } from '../../../../integrations/lavva/modules/action-create/utils/action-tasks';
import { TaskItemWrapper, TaskItemContainer, IconWrapper, TaskItemContent, TaskItemTitle, TaskItemText } from './task-item.styled';

type TaskItemProps = {
  task: Task;
};

const TaskItem: React.FC<TaskItemProps> = ({ task }) => {
  const navigate = useNavigate();
  const { t } = useTranslation('action');
  const { setTaskVariant, setChannels, setTaskType, removeTask } = useLavvaCreateActionContext();
  const { getTaskState } = useActionDetailsPresentation();

  const handleDelete = () => removeTask(task.id);

  const editParameters = () => {
    setTaskType(task.taskType);

    switch (task.taskType) {
      case SceneTaskType.ChannelState: {
        setChannels([(task.taskParams as DeviceTaskParams).channel]);
        setTaskVariant((task.taskParams as DeviceTaskParams).variant);
        navigate(`${ROUTES.ActionChannelSettings()}?id=${task.id}`);
        break;
      }
      default: {
        break;
      }
    }
  };

  const value = useMemo(() => getTaskState(task), [task]);

  const taskOption = useMemo(() => {
    if (task.taskType === SceneTaskType.ChannelState) {
      return actionTaskVariants.find((x) => x.variant === (task.taskParams as DeviceTaskParams).variant);
    }

    return actionTasks.find((x) => x.type === task.taskType);
  }, [task]);

  const taskTitle = useMemo(() => {
    switch (task.taskType) {
      case SceneTaskType.ChannelState:
        return (task.taskParams as DeviceTaskParams).channel.alias;
      default:
        return '';
    }
  }, [task]);

  return (
    <TaskItemWrapper>
      <TaskItemContainer>
        <IconWrapper>{taskOption?.icon}</IconWrapper>
        <TaskItemContainer>
          <TaskItemContent>
            <TaskItemTitle>{taskTitle}</TaskItemTitle>
            <TaskItemText>{value}</TaskItemText>
          </TaskItemContent>
        </TaskItemContainer>
      </TaskItemContainer>
      <PopUpNav
        dark
        links={[
          ...(task.taskParams.variant !== TaskVariant.SetVentilation
            ? [
                {
                  onClick: editParameters,
                  label: t('action.create.conditions.editConditionParameters'),
                  icon: <IconEdit />,
                },
              ]
            : []),
          {
            onClick: handleDelete,
            label: t('remove'),
            icon: <IconDelete />,
          },
        ]}
      />
    </TaskItemWrapper>
  );
};

export default TaskItem;
